import { BlockCard, DeltaBadge, SegmentedPills } from "../primitives";
import type { PillOption } from "../primitives";
import { BlockBaseProps, Delta } from "../types";
import TopBooksBlockSkeleton from "./Skeleton";
import styles from "./style.module.scss";

export interface BookRow {
  id: string | number;
  rank: number;
  title: string;
  author: string;
  language: string;
  age: string;
  /** Cover colour; the design uses a flat swatch with the title's initials. */
  cover: string;
  /** Real cover image; falls back to the colour swatch when absent. */
  coverPhoto?: string;
  initials: string;
  reads: string;
  finished: string;
  finishPct: string;
  delta?: Delta;
}

interface TopBooksBlockProps extends BlockBaseProps {
  books: BookRow[];
  sorts?: PillOption[];
  sort?: string;
  onSortChange?: (key: string) => void;
  onSelectBook?: (id: string | number) => void;
  title?: string;
}

/** The most-read titles, sortable by reads or by finishes. */
function TopBooksBlock({
  books,
  sorts,
  sort,
  onSortChange,
  onSelectBook,
  title = "Top books",
  loading,
  empty,
  className,
}: TopBooksBlockProps) {
  if (loading) {
    return (
      <TopBooksBlockSkeleton count={books.length || 5} className={className} />
    );
  }

  return (
    <BlockCard
      empty={empty}
      flush
      title={title}
      className={className}
      actions={
        sorts &&
        sort &&
        onSortChange && (
          <SegmentedPills
            options={sorts}
            value={sort}
            onChange={onSortChange}
            shape="box"
          />
        )
      }
    >
      <div className={styles.list}>
        {books.map((book) => (
          <div
            key={book.id}
            role={onSelectBook ? "button" : undefined}
            tabIndex={onSelectBook ? 0 : undefined}
            onClick={() => onSelectBook?.(book.id)}
            onKeyDown={(event) => {
              if (onSelectBook && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault();
                onSelectBook(book.id);
              }
            }}
            className={styles.row}
            data-clickable={onSelectBook ? "true" : undefined}
          >
            <span className={styles.rank}>{book.rank}</span>
            <div
              className={`${styles.cover} ${
                book.coverPhoto ? styles.coverImage : ""
              }`}
              // Inline, so it would outrank any stylesheet rule - only paint the
              // swatch colour when there is no photo to sit on top of it.
              style={{ background: book.coverPhoto ? undefined : book.cover }}
            >
              {book.coverPhoto ? (
                <img
                  src={book.coverPhoto}
                  alt={book.title}
                  className={styles.coverPhoto}
                  loading="lazy"
                />
              ) : (
                book.initials
              )}
            </div>
            <div className={styles.meta}>
              <div className={styles.title}>{book.title}</div>
              <div className={styles.sub}>
                {`${book.author} · ${book.language} · ${book.age}`}
              </div>
              <div className={styles.finished}>
                {`${book.finished} finished `}
                <span className={styles.finishPct}>{`(${book.finishPct})`}</span>
              </div>
            </div>
            <div className={styles.figures}>
              <div className={styles.reads}>{book.reads}</div>
              <div className={styles.readsLabel}>reads</div>
              <DeltaBadge
                delta={book.delta}
                bare
                size="sm"
                className={styles.delta}
              />
            </div>
          </div>
        ))}
      </div>
    </BlockCard>
  );
}

export default TopBooksBlock;
